import React from 'react';
import { Helmet } from 'react-helmet-async';
import { getLandmarkUrl } from '../lib/landmarkImages';

const PROD_URL = "https://paisley.org.uk";
// Default OG image — the 1200x900 copy in the media bucket rather than the
// 1.5MB original in /public: it is a seventh of the weight and a plain JPEG,
// where the original is an MPO that some share scrapers decline to render.
const DEFAULT_IMAGE = "https://media.paisley.org.uk/site/og-paisley-abbey.jpg";

// Google truncates SERP titles at ~60 chars. Keep our <title> tag under this
// so it's not chopped mid-word in search results.
const MAX_TITLE_CHARS = 60;

/**
 * Trim a title to fit the SERP width without breaking a word. Prefers the
 * cleanest suffix removal (drops "| ..." site-name tails first, then falls
 * back to a soft word-boundary truncation with an ellipsis.
 */
const _seoTruncate = (raw) => {
  if (!raw) return "";
  let t = String(raw).trim();
  if (t.length <= MAX_TITLE_CHARS) return t;

  // 1) If the title has a " | <suffix>" tail, drop the suffix first.
  const pipeAt = t.lastIndexOf(" | ");
  if (pipeAt > 0 && pipeAt <= MAX_TITLE_CHARS) return t.slice(0, pipeAt).trim();

  // 2) Same for " — <suffix>"
  const dashAt = t.lastIndexOf(" — ");
  if (dashAt > 0 && dashAt <= MAX_TITLE_CHARS) return t.slice(0, dashAt).trim();

  // 3) Otherwise soft-wrap at the last space before the limit.
  const cut = t.lastIndexOf(" ", MAX_TITLE_CHARS - 1);
  if (cut > 30) return t.slice(0, cut).trim() + "…";
  return t.slice(0, MAX_TITLE_CHARS - 1) + "…";
};

// NewsMediaOrganization JSON-LD — emitted once, on the homepage only, via the
// `isHomepage` flag. Keeps schema markup centralised and editable in one place.
const NEWS_MEDIA_ORG_LD = {
  "@context": "https://schema.org",
  "@type": "NewsMediaOrganization",
  "name": "Paisley.org.uk",
  "url": PROD_URL,
  "logo": `${PROD_URL}/logo512.png`,
  "description": "Local news, events, history and community guide for Paisley, Renfrewshire, Scotland.",
  "email": "brian@paisley.org.uk",
  "telephone": "+44 7957 166339",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "14 High Street",
    "addressLocality": "Paisley",
    "addressRegion": "Renfrewshire",
    "postalCode": "PA1 2BS",
    "addressCountry": "GB",
  },
  "areaServed": {
    "@type": "Place",
    "name": "Paisley, Renfrewshire, Scotland",
  },
  "sameAs": [
    "https://www.facebook.com/paisleyorguk",
    "https://twitter.com/paisleyorguk",
    "https://www.instagram.com/paisleyscotland",
    "https://www.tiktok.com/@paisleyscotland",
    "https://www.youtube.com/@paisleyscotland",
    "https://www.linkedin.com/company/paisleyontheweb",
  ],
};

/**
 * Universal SEO component.
 * - Renders title, meta description, canonical, Open Graph, Twitter Card.
 * - For posts/articles, pass type="article" plus publishedTime/modifiedTime/category
 *   to emit article-specific OG tags and a NewsArticle JSON-LD block.
 */
const SEO = ({
  title = "Paisley.org.uk - Your Guide to Paisley, Scotland",
  description = "Discover Paisley's rich history, events, attractions, and community. Your comprehensive guide to Scotland's largest town in Renfrewshire.",
  keywords = "Paisley, Scotland, Paisley Abbey, events, history, attractions, Renfrewshire",
  image = DEFAULT_IMAGE,
  url = PROD_URL,
  type = "website",
  author = "Paisley.org.uk",
  publishedTime,
  modifiedTime,
  category,
  noIndex = false,
  isHomepage = false,
  emitArticleSchema = true,
  jsonLd = null,
}) => {
  // For article pages, append " · Paisley News" so the <title> differs from
  // the on-page <h1> (Semrush flagged 5 exact H1↔title matches). We only add
  // the suffix if it fits within the 60-char SERP budget, and never when the
  // caller already supplied their own section suffix — event pages were
  // sharing as "Title | Paisley Events · Paisley News".
  const _articleSuffix = " · Paisley News";
  const _alreadySuffixed = title.includes(_articleSuffix) || title.includes("|") || title.includes("·");
  const _rawTitle = type === "article" && !_alreadySuffixed && (title.length + _articleSuffix.length) <= MAX_TITLE_CHARS
    ? `${title}${_articleSuffix}`
    : title;
  const fullTitle = _seoTruncate(_rawTitle);
  // og:image fallback — if the caller didn't supply an image (or supplied
  // the default), pick a landmark based on the page URL so social share
  // previews stay stable per-page and no post ever shares with the same
  // generic hero.
  const _isDefaultImg = !image || image === DEFAULT_IMAGE;
  const _resolvedImage = _isDefaultImg ? getLandmarkUrl(url || title) : image;
  const absoluteImage = _resolvedImage && !_resolvedImage.startsWith("http")
    ? `${PROD_URL}${_resolvedImage}`
    : _resolvedImage;
  const absoluteUrl = url && !url.startsWith("http") ? `${PROD_URL}${url}` : url;

  // NewsArticle structured data for posts. Event pages pass
  // emitArticleSchema={false}: an event is not a news article, and emitting
  // both types for one URL is contradictory markup.
  const articleJsonLd = type === "article" && emitArticleSchema ? {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "headline": title,
    "description": description,
    "image": [absoluteImage],
    "datePublished": publishedTime,
    "dateModified": modifiedTime || publishedTime,
    "author": {
      "@type": "Organization",
      "name": author,
      "url": PROD_URL
    },
    "publisher": {
      "@type": "Organization",
      "name": "Paisley.org.uk",
      "logo": {
        "@type": "ImageObject",
        "url": `${PROD_URL}/logo512.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": absoluteUrl
    },
    ...(category ? { "articleSection": category } : {})
  } : null;

  return (
    <Helmet prioritizeSeoTags>
      {/* Basic */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <link rel="canonical" href={absoluteUrl} />
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1"} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:image:secure_url" content={absoluteImage} />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:url" content={absoluteUrl} />
      <meta property="og:site_name" content="Paisley.org.uk" />
      <meta property="og:locale" content="en_GB" />

      {/* Article-specific OG */}
      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === "article" && author && (
        <meta property="article:author" content={author} />
      )}
      {type === "article" && category && (
        <meta property="article:section" content={category} />
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@paisleyorguk" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImage} />
      <meta name="twitter:image:alt" content={title} />

      {/* RSS / Sitemap discovery */}
      <link rel="alternate" type="application/rss+xml" title="Paisley.org.uk RSS Feed" href={`${PROD_URL}/api/rss`} />

      {/* JSON-LD */}
      {articleJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(articleJsonLd)}
        </script>
      )}
      {/* NewsMediaOrganization is emitted once, statically, in public/index.html
          so it is present in the initial response rather than only after React
          runs. Emitting it here too produced two identical blocks. */}
      {jsonLd && (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).map((node, i) => (
        <script type="application/ld+json" key={`ld-${i}`}>
          {JSON.stringify(node)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
